import type { DashboardQuery } from "~/features/dashboard/query.generated.ts";
import countryOptions from "../../utils/country-options.ts";

/**
 * Map of country codes to their full country information
 */
const countryMap = new Map(
	countryOptions.map((country) => [country.value, country]),
);

/**
 * Enum for common time durations in milliseconds
 */

const SECOND = 1000;
const MINUTE = 60 * SECOND;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;
const WEEK = 7 * DAY;
const MONTH = 30 * DAY;
const YEAR = 365 * DAY;

const TimeInMilliseconds = {
	SECOND,
	MINUTE,
	HOUR,
	DAY,
	WEEK,
	MONTH,
	YEAR,
};

/**
 * Convert an integer date (YYYYMMDD) to a string date "YYYY/MM/DD"
 */
function integerDateToString(date: number) {
	return date.toString().replace(/(\d{4})(\d{2})(\d{2})/, "$1/$2/$3");
}

/**
 * Convert a date object to analytics request date format ("YYYY-MM-DD")
 * Example: new Date("2025-03-20") -> "2025-03-20"
 */
function dateToAnalyticsDateFormat(date: Date) {
	return date.toISOString().split("T")[0];
}

/**
 * Convert a Date object to an integer in YYYYMMDD format
 * Example: new Date("2025-03-20") -> 20250320
 */
function dateToInteger(date: Date): number {
	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, "0");
	const day = String(date.getDate()).padStart(2, "0");
	return parseInt(`${year}${month}${day}`, 10);
}

// Define the structure for the chart data returned by the transform function
type ChartData = {
	labels: string[];
	datasets: Array<{
		label: string;
		data: number[];
		borderColor: string;
		backgroundColor: string;
		tension: number;
		borderWidth?: number;
	}>;
};

type TransformedAnalyticsData = {
	newUsersChartData: ChartData;
	savedProjectsChartData: ChartData;
	cartProjectsChartData: ChartData;
	orderedProjectsChartData: ChartData;
	combinedProjectsChartData: ChartData;
};

/**
 * Transform analytics data into chart.js format for both new users and saved projects
 */
function transformAnalyticsData(
	data: DashboardQuery,
): TransformedAnalyticsData {
	const labels = data.analytics.map((item) => integerDateToString(item.id));

	// Define base colors for project types
	const savedProjectColors = {
		solid: "rgb(75, 192, 192)",
		light: "rgba(75, 192, 192, 0.5)",
	};
	const cartProjectColors = {
		solid: "rgb(54, 162, 235)",
		light: "rgba(54, 162, 235, 0.5)",
	};
	const orderedProjectColors = {
		solid: "rgb(153, 102, 255)",
		light: "rgba(153, 102, 255, 0.5)",
	};
	// A distinct color for the "unique users" line in the combined chart for users who only ordered (not in cart or saved priorly)
	// This might be too complex for the current data structure, let's stick to project-type colors for unique lines for now.
	// const uniqueUsersOrderedOnlyColor = { solid: "rgb(255, 159, 64)", light: "rgba(255, 159, 64, 0.5)" };

	// --- Process New Users ---
	const countries = new Set<string>();
	const newUserCountryTotals = new Map<string, number>();
	data.analytics.forEach((day) => {
		day.newUsers.forEach((user) => {
			if (user.ipAddressCountry) {
				countries.add(user.ipAddressCountry);
				newUserCountryTotals.set(
					user.ipAddressCountry,
					(newUserCountryTotals.get(user.ipAddressCountry) || 0) + 1,
				);
				newUserCountryTotals.set(
					"total",
					(newUserCountryTotals.get("total") || 0) + 1,
				);
			}
		});
	});

	// Create datasets for each country
	const newUserCountryDatasets = Array.from(countries, (countryCode) => {
		const userCounts = data.analytics.map(
			(day) =>
				day.newUsers.filter((user) => user.ipAddressCountry === countryCode)
					.length,
		);

		const countryInfo = countryMap.get(countryCode);
		const borderColor = countryInfo?.color || "#000000";
		const backgroundColor = countryInfo?.secondaryColor || "#000000";

		return {
			label: `${countryInfo?.flag} ${countryInfo?.label} ${newUserCountryTotals.get(countryCode)}`,
			data: userCounts,
			borderColor,
			backgroundColor,
			tension: 0.1,
		};
	});

	// Add total dataset
	const totalNewUserData = data.analytics.map((day) => day.newUsers.length);
	const totalNewUserDataset = {
		label: `Total ${newUserCountryTotals.get("total")}`,
		data: totalNewUserData,
		borderColor: "rgb(0, 0, 0)",
		backgroundColor: "rgb(0, 0, 0)",
		tension: 0.1,
		borderWidth: 2,
	};

	const newUsersChartData: ChartData = {
		labels,
		datasets: [...newUserCountryDatasets, totalNewUserDataset],
	};

	// --- Process Saved Projects ---
	// Note: Saved projects don't have country info, so we just aggregate total count
	const savedProjectsData = data.analytics.map(
		// Use 'as any' to access potentially missing property, default to 0
		(day) => (day as any).savedProjects?.length || 0,
	);
	const uniqueUsersSavingProjectsData = data.analytics.map((day) => {
		// Use 'as any' to access potentially missing property
		const projects = (day as any).savedProjects;
		if (!projects || projects.length === 0) {
			return 0;
		}
		// Count unique user IDs
		const userIds = new Set(projects.map((p: { userId: string }) => p.userId));
		return userIds.size;
	});

	const totalSavedProjects = savedProjectsData.reduce(
		(sum, count) => sum + count,
		0,
	);
	const totalUniqueUsersSaving = uniqueUsersSavingProjectsData.reduce(
		(sum, count) => sum + count,
		0,
	);

	// const baseTotalProjectsDatasetConfig = { // This will be replaced by specific configs
	// 	borderColor: "rgb(75, 192, 192)",
	// 	backgroundColor: "rgba(75, 192, 192, 0.5)",
	// 	tension: 0.1,
	// 	borderWidth: 2,
	// };

	// const baseUniqueUsersDatasetConfig = { // This will be replaced by specific configs
	// 	borderColor: "rgb(255, 99, 132)",
	// 	backgroundColor: "rgba(255, 99, 132, 0.5)",
	// 	tension: 0.1,
	// 	borderWidth: 1,
	// 	borderDash: [5, 5], // Make it dashed to distinguish
	// };

	const savedProjectsDataset = {
		// ...baseTotalProjectsDatasetConfig,
		label: `Total Saved Projects (${totalSavedProjects})`,
		data: savedProjectsData,
		borderColor: savedProjectColors.solid,
		backgroundColor: savedProjectColors.light,
		tension: 0.1,
		borderWidth: 2,
	};

	const uniqueUsersDataset = {
		// ...baseUniqueUsersDatasetConfig,
		label: `Unique Users Saving (${totalUniqueUsersSaving})`,
		data: uniqueUsersSavingProjectsData,
		borderColor: savedProjectColors.solid, // Same base color
		backgroundColor: savedProjectColors.light, // Can use light or solid, border is key
		tension: 0.1,
		borderWidth: 1,
		borderDash: [5, 5], // Dashed
	};

	const savedProjectsChartData: ChartData = {
		labels,
		datasets: [savedProjectsDataset, uniqueUsersDataset],
	};

	// --- Process Cart Projects ---
	const cartProjectsData = data.analytics.map(
		(day) => (day as any).cartProjects?.length || 0,
	);
	const uniqueUsersCartingData = data.analytics.map((day) => {
		const projects = (day as any).cartProjects;
		if (!projects || projects.length === 0) {
			return 0;
		}
		const userIds = new Set(projects.map((p: { userId: string }) => p.userId));
		return userIds.size;
	});

	const totalCartProjects = cartProjectsData.reduce(
		(sum, count) => sum + count,
		0,
	);
	const totalUniqueUsersCarting = uniqueUsersCartingData.reduce(
		(sum, count) => sum + count,
		0,
	);

	const cartProjectsDataset = {
		borderColor: cartProjectColors.solid,
		backgroundColor: cartProjectColors.light,
		tension: 0.1,
		borderWidth: 2,
		label: `Total Cart Projects (${totalCartProjects})`,
		data: cartProjectsData,
	};

	const uniqueUsersCartingDataset = {
		borderColor: cartProjectColors.solid, // Same base color
		backgroundColor: cartProjectColors.light,
		tension: 0.1,
		borderWidth: 1,
		borderDash: [5, 5], // Dashed
		label: `Unique Users Carting (${totalUniqueUsersCarting})`,
		data: uniqueUsersCartingData,
	};

	const cartProjectsChartData: ChartData = {
		labels,
		datasets: [cartProjectsDataset, uniqueUsersCartingDataset],
	};

	// --- Process Ordered Projects ---
	const orderedProjectsData = data.analytics.map(
		(day) => (day as any).orderedProjects?.length || 0,
	);
	const uniqueUsersOrderingData = data.analytics.map((day) => {
		const projects = (day as any).orderedProjects;
		if (!projects || projects.length === 0) {
			return 0;
		}
		const userIds = new Set(projects.map((p: { userId: string }) => p.userId));
		return userIds.size;
	});

	const totalOrderedProjects = orderedProjectsData.reduce(
		(sum, count) => sum + count,
		0,
	);
	const totalUniqueUsersOrdering = uniqueUsersOrderingData.reduce(
		(sum, count) => sum + count,
		0,
	);

	const orderedProjectsDataset = {
		// ...baseTotalProjectsDatasetConfig, // No longer using base
		borderColor: orderedProjectColors.solid,
		backgroundColor: orderedProjectColors.light,
		label: `Total Ordered Projects (${totalOrderedProjects})`,
		data: orderedProjectsData,
		tension: 0.1,
		borderWidth: 2,
	};

	const uniqueUsersOrderingDataset = {
		// ...baseUniqueUsersDatasetConfig, // No longer using base
		borderColor: orderedProjectColors.solid, // Same base color
		backgroundColor: orderedProjectColors.light,
		label: `Unique Users Ordering (${totalUniqueUsersOrdering})`,
		data: uniqueUsersOrderingData,
		tension: 0.1,
		borderWidth: 1,
		borderDash: [5, 5], // Dashed
	};

	const orderedProjectsChartData: ChartData = {
		labels,
		datasets: [orderedProjectsDataset, uniqueUsersOrderingDataset],
	};

	// --- Process Combined Projects ---
	const totalSavedProjectsCombinedDataset = {
		label: `Total Saved (${totalSavedProjects})`,
		data: savedProjectsData,
		borderColor: savedProjectColors.solid,
		backgroundColor: savedProjectColors.light,
		tension: 0.1,
		borderWidth: 2,
	};
	const uniqueUsersSavedCombinedDataset = {
		label: `Unique Users Saving (${totalUniqueUsersSaving})`,
		data: uniqueUsersSavingProjectsData,
		borderColor: savedProjectColors.solid,
		backgroundColor: savedProjectColors.light,
		tension: 0.1,
		borderWidth: 1,
		borderDash: [5, 5],
	};

	const totalCartProjectsCombinedDataset = {
		label: `Total Cart (${totalCartProjects})`,
		data: cartProjectsData,
		borderColor: cartProjectColors.solid,
		backgroundColor: cartProjectColors.light,
		tension: 0.1,
		borderWidth: 2,
	};
	const uniqueUsersCartCombinedDataset = {
		label: `Unique Users Carting (${totalUniqueUsersCarting})`,
		data: uniqueUsersCartingData,
		borderColor: cartProjectColors.solid,
		backgroundColor: cartProjectColors.light,
		tension: 0.1,
		borderWidth: 1,
		borderDash: [5, 5],
	};

	const totalOrderedProjectsCombinedDataset = {
		label: `Total Ordered (${totalOrderedProjects})`,
		data: orderedProjectsData,
		borderColor: orderedProjectColors.solid,
		backgroundColor: orderedProjectColors.light,
		tension: 0.1,
		borderWidth: 2,
	};
	const uniqueUsersOrderedCombinedDataset = {
		label: `Unique Users Ordering (${totalUniqueUsersOrdering})`,
		data: uniqueUsersOrderingData,
		borderColor: orderedProjectColors.solid,
		backgroundColor: orderedProjectColors.light,
		tension: 0.1,
		borderWidth: 1,
		borderDash: [5, 5],
	};

	const combinedProjectsChartData: ChartData = {
		labels,
		datasets: [
			totalSavedProjectsCombinedDataset,
			uniqueUsersSavedCombinedDataset, // Added
			totalCartProjectsCombinedDataset,
			uniqueUsersCartCombinedDataset, // Added
			totalOrderedProjectsCombinedDataset,
			uniqueUsersOrderedCombinedDataset, // Added
		],
	};

	return {
		newUsersChartData,
		savedProjectsChartData,
		cartProjectsChartData,
		orderedProjectsChartData,
		combinedProjectsChartData,
	};
}

const newUsersChartOptions = {
	plugins: {
		legend: {
			position: "top" as const,
		},
		title: {
			display: true,
			text: "Daily User Signups by Country",
		},
	},
	responsive: true,
	scales: {
		y: {
			ticks: {
				// Ensure ticks are integers
				stepSize: 1,
				callback: function (value: string | number) {
					if (Number.isInteger(value)) {
						return value;
					}
				},
			},
			min: 0,
		},
	},
};

const generateProjectsChartOptions = (label: string) => ({
	responsive: true,
	scales: {
		y: {
			ticks: {
				stepSize: 1,
				callback: function (value: string | number) {
					if (Number.isInteger(value)) {
						return value;
					}
				},
			},
			min: 0,
		},
	},
	plugins: {
		legend: {
			position: "top" as const,
		},
		title: {
			display: true,
			text: label,
		},
	},
});

// Options for the Saved Projects chart (can be customized further)
const savedProjectsChartOptions = generateProjectsChartOptions(
	"Daily Saved Projects",
);

const cartProjectsChartOptions = generateProjectsChartOptions(
	"Daily Cart Projects",
);

const orderedProjectsChartOptions = generateProjectsChartOptions(
	"Daily Ordered Projects",
);

const combinedProjectsChartOptions = generateProjectsChartOptions(
	"Daily Project Activity (Saved, Cart, Ordered)",
);

export {
	TimeInMilliseconds,
	integerDateToString,
	dateToAnalyticsDateFormat,
	dateToInteger,
	transformAnalyticsData,
	newUsersChartOptions,
	savedProjectsChartOptions,
	cartProjectsChartOptions,
	orderedProjectsChartOptions,
	combinedProjectsChartOptions,
};
